import { useMutation as useQueryMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState, useCallback } from "react";
import OpusMediaRecorder from "opus-media-recorder";

const DEFAULT_OPTIONS = {
  maxPause: 2000,
  minDecibels: -45,
};

type Options = {
  maxPause?: number;
  minDecibels?: number;
  onStart?: () => void;
  onStop?: () => void;
  onCancel?: () => void;
};

export const useRecording = (options: Options = DEFAULT_OPTIONS) => {
  const {
    maxPause,
    minDecibels,
    onStart: onStartCallback,
    onStop: onStopCallback,
    onCancel: onCancelCallback,
  } = { ...DEFAULT_OPTIONS, ...options };
  const [havePermissions, setHavePermissions] = useState<boolean>();
  const mediaRecorderRef = useRef<any>();
  const canceled = useRef(false);
  const animFrame = useRef<any>();
  const silenceTimer = useRef<number | null>(null);
  const chunks = useRef<any>([]);
  const [isRecording, setIsRecording] = useState(false);
  const {
    data,
    mutateAsync: getRecordingData,
    isLoading: isProcessing,
    reset,
  } = useQueryMutation({
    mutationFn: async (chunks: any) => {
      const audioBlob = new Blob(chunks, {
        type: "audio/webm; codecs=opus",
      });
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      return await new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          const base64data = reader.result;
          if (base64data && base64data !== "data:") {
            resolve(base64data);
          } else {
            reject("Error: base64data is undefined or empty.");
          }
        };
      });
    },
  });

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?._state === "recording") {
      mediaRecorderRef.current.stop();
      silenceTimer.current = null;
    }
  }, [getRecordingData, chunks]);

  const resetRecording = useCallback(() => {
    stopRecording();
    chunks.current = [];
    reset();
  }, [stopRecording]);

  const cancelRecording = useCallback(() => {
    canceled.current = true;
    onCancelCallback?.();
    resetRecording();
  }, [resetRecording, onCancelCallback]);

  const startRecording = useCallback(() => {
    if (mediaRecorderRef.current?._state !== "recording") {
      chunks.current = [];
      silenceTimer.current = null;
      reset();
      mediaRecorderRef.current.start();
    }
  }, []);

  useEffect(() => {
    return () => {
      window.cancelAnimationFrame(animFrame.current);
    };
  }, []);

  const startSilenceTimeout = useCallback(() => {
    if (silenceTimer.current) {
      // console.log("Silence for: ", (Date.now() - silenceTimer.current) / 1000);
      if (Date.now() - silenceTimer.current > maxPause) {
        stopRecording();
      }
      return;
    }
    silenceTimer.current = Date.now();
  }, [stopRecording, maxPause]);

  const stopSilenceTimeout = useCallback(() => {
    silenceTimer.current = null;
  }, []);

  const checkAudioLevel = useCallback((stream: any) => {
    const audioContext = new AudioContext();
    const audioStreamSource = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    // by default maxDecibels is -30bB and throws INDEX_SIZE_ERR when minDecibels is the same
    analyser.maxDecibels = -10;
    analyser.minDecibels = minDecibels;
    audioStreamSource.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const domainData = new Uint8Array(bufferLength);

    const detectSound = () => {
      let soundDetected = false;
      const isRecording = mediaRecorderRef.current._state === "recording";
      analyser.getByteFrequencyData(domainData);

      for (let i = 0; i < bufferLength; i++) {
        if (domainData[i] > 0) {
          soundDetected = true;
          break;
        }
      }

      if (soundDetected) {
        stopSilenceTimeout();
      } else {
        if (isRecording) {
          startSilenceTimeout();
        }
      }

      animFrame.current = window.requestAnimationFrame(detectSound);
    };

    animFrame.current = window.requestAnimationFrame(detectSound);
  }, []);

  useEffect(() => {
    const options = { mimeType: "audio/webm; codecs=opus" };

    const workerOptions = {
      encoderWorkerFactory: function () {
        // UMD should be used if you don't use a web worker bundler for this.
        return new Worker("/opus-media-recorder/encoderWorker.umd.js");
      },
      OggOpusEncoderWasmPath: "/opus-media-recorder/OggOpusEncoder.wasm",
      WebMOpusEncoderWasmPath: "/opus-media-recorder/WebMOpusEncoder.wasm",
    };

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const mediaRecorder = new OpusMediaRecorder(
          stream,
          options,
          workerOptions
        );
        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.ondataavailable = onData;
        mediaRecorder.onstop = onStop;
        mediaRecorder.onstart = onStart;
        checkAudioLevel(stream);
        setHavePermissions(true);
      })
      .catch((error) => {
        console.error("Error getting user media:", error);
        setHavePermissions(false);
      });
  }, []);

  const onData = useCallback((e: any) => {
    chunks.current.push(e.data);
  }, []);

  const onStart = useCallback(() => {
    stopSilenceTimeout();
    onStartCallback?.();
    setIsRecording(true);
  }, [setIsRecording, onStartCallback]);

  const onStop = useCallback(() => {
    onStopCallback?.();
    setIsRecording(false);
    if (canceled.current) {
      canceled.current = false;
      return;
    }
    getRecordingData(chunks.current);
  }, [setIsRecording, onStopCallback, getRecordingData]);

  return {
    havePermissions,
    initialized: havePermissions !== undefined,
    data: data as string | null,
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    cancelRecording,
    reset: resetRecording,
  };
};
